import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Loader from "../../components/loaders/Loader";
import Tabs, { TabButton } from "../../components/tabs/Tabs";
import { fetchPublicPricing } from "../../services/api/membership";
import { PricingData, SubscriptionFrequencies, Plan, SubscriptionFeature, FrequencyDetail } from "../../types/membership/membershipTypes";
import { toValidNumber } from "../../utils/helpers";
import { useMediaQuery } from "react-responsive";
import { isDesktopMediaQuery } from "../../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux-stores/store";
import { setBGWhite, setIntendedPath } from "../../redux-stores/slices/layoutSlice";
import PricingPageHandRight from "../../assets/images/headers/pricing-hand-right.png";
import { Swiper, SwiperSlide } from "swiper/react";
import 'swiper/css';
import 'swiper/css/pagination';
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import { Avatar, AvatarGroup } from "@chatscope/chat-ui-kit-react";
import { isIOS } from "../../utils/platformUtil";

const PricingPublic: React.FC = () => {
    const location = useLocation();

    const [loading, setLoading] = useState<boolean>(false);
    const [pricing, setPricing] = useState<PricingData | null>(null);
    const [frequency, setFrequency] = useState<SubscriptionFrequencies>(SubscriptionFrequencies.MONTHLY);
    const [durationWisePricing, setDurationWisePricing] = React.useState<{ plan: Plan, frequency: SubscriptionFrequencies } | null>(null);
    const dispatch = useDispatch();

    const isDesktop = useMediaQuery(isDesktopMediaQuery);

    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();

    const fetchPricingData = async () => {
        try {
            setLoading(true);
            const pricingData = await fetchPublicPricing();
            setPricing(pricingData);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (durationWisePricing) {
            dispatch(setBGWhite(true));
        } else {
            dispatch(setBGWhite(false));
        }
    }, [durationWisePricing])

    useEffect(() => {
        fetchPricingData();
    }, [])

    useEffect(() => {
        if (isAuthenticated) {
            navigate("/i/pricing");
        }
    }, [isAuthenticated])

    const getSortedFrequenciesByPrice = (plan: Plan) => {
        if (!plan.frequencies) return [];

        const frequenciesArray: (FrequencyDetail & { slug: string })[] = [];

        for (let [freq, details] of Object.entries(plan.frequencies)) {
            frequenciesArray.push({ ...details, slug: freq });
        }

        return frequenciesArray.sort((a, b) => Number(a.amount) - Number(b.amount));

    }

    const calculateDiscount = (originalAmount: number, amount: number) => {
        return (originalAmount - amount).toFixed(0);
    }



    const getFrequenciesDataAsTabButtons = () => {
        let buttons: TabButton[] = [];
        if (pricing?.frequencies) {
            for (let [id, content] of Object.entries(pricing.frequencies)) {
                buttons.push({ id, content })
            }
        }
        return buttons;
    }

    const updateIntendedURL = () => {
        dispatch(setIntendedPath(location.pathname + location.search));
    }


    if (loading) return <Loader isLoading={true} />

    return (
        <div className="min-full-screen">
            <div className="pricing-page-header mt-0">
                <img src={PricingPageHandRight} alt="header-graphic" className="header-graphic" />
            </div>
            {
                durationWisePricing ? (
                    <>
                        <div className="m-3">
                            <div className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                <button onClick={() => setDurationWisePricing(null)} className="text-dark btn-link"> <i className="fa fa-arrow-left fs-14"></i> </button>
                                <h6 className="m-0">{durationWisePricing?.plan.name}</h6>
                            </div>
                            <p>{durationWisePricing?.plan?.description}</p>
                            {
                                getSortedFrequenciesByPrice(durationWisePricing?.plan)?.map((freq) => (
                                    <div
                                        key={freq.id}
                                        onClick={() => setDurationWisePricing({ plan: durationWisePricing.plan, frequency: freq.slug as SubscriptionFrequencies })}
                                        className={`cursor-pointer p-3 mb-3 rounded-4 position-relative ${freq.slug === durationWisePricing.frequency ? "bg-primary text-white" : "border border-dark"}`}
                                        style={{ borderRadius: "18px" }}
                                    >

                                        {/* Discount Badge */}
                                        {freq.discount > 0 && (
                                            <span
                                                className="position-absolute translate-middle badge bg-success text-white fs-14 py-2"
                                                style={{ borderRadius: "1rem", right: '-25px', }}
                                            >
                                                -{freq.discount}%
                                            </span>
                                        )}

                                        {/* Title */}
                                        <div className="fw-semibold small">{freq.name}</div>

                                        {/* Amount Row */}
                                        <div className="d-flex align-items-center gap-2 mt-1">
                                            <div className="fs-4 fw-bold">₹{freq.amount}</div>

                                            {freq.original_amount && Number(freq.original_amount) > Number(freq.amount) && (
                                                <div className="text-decoration-line-through fs-14">
                                                    ₹{freq.original_amount}
                                                </div>
                                            )}
                                            {
                                                freq.slug !== SubscriptionFrequencies.MONTHLY &&
                                                <div className="fs-14">
                                                    (₹{freq.monthly_amount} / month)
                                                </div>
                                            }
                                        </div>
                                    </div>
                                ))
                            }
                            <Link onClickCapture={updateIntendedURL} style={{ height: '48px' }} className="btn btn-primary rounded-4 bg-primary border-primary w-100 d-flex align-items-center gap-2 justify-content-center" to={'/register'}>Join Now</Link>
                        </div>
                    </>
                ) :
                    (
                        <>
                            <div className="d-flex flex-column bg-white">
                                <h1 className="pricing-heading mx-auto">Pick Your Plan</h1>
                                {/* <p className="mx-auto ">Select a plan that fits your needs</p> */}
                                <div className="d-flex flex-column align-items-center mt-1 my-3 gap-2">
                                    <span className="fs-12 fw-400 text-muted">Trusted by 200k creators</span>
                                    {
                                        pricing?.membership_profiles?.pictures && pricing.membership_profiles.pictures.length > 0 &&
                                        <>
                                            <AvatarGroup>
                                                {
                                                    pricing.membership_profiles.pictures.map((pic, index) => (
                                                        <Avatar key={index} src={pic} name={`User ${index + 1}`} />
                                                    ))
                                                }
                                            </AvatarGroup>
                                        </>
                                    }
                                </div>
                                <div className="">
                                    {/* <Tabs buttons={getFrequenciesDataAsTabButtons()} buttonClass="mx-3" onChange={(fq: SubscriptionFrequencies) => setFrequency(fq)} /> */}
                                    <Swiper className="mySwiper pending-tasks-swiper" spaceBetween={16} slidesPerView={1.1}>
                                        {
                                            pricing?.plans && pricing.plans.map((plan, i) => {
                                                return (
                                                    <SwiperSlide key={i}>
                                                        <div className={`app-card overflow-hidden plan-card ${plan.name.toLowerCase()} my-3 ${i === 0 ? 'ms-5' : (i === pricing.plans.length - 1 ? 'me-5' : 'mx-3')} position-relative ${plan.featured ? 'border border-green-dark' : ''} p-0`}>
                                                            {/* {
                                                            (plan.id === pricing.active_plan?.plan.id) ? (
                                                                null
                                                            ) : plan.featured ? (
                                                                <span className="featured-badge">Popular</span>
                                                            ) : null
                                                        } */}
                                                            <div className={`plan-card-header ${plan.name.toLowerCase()}`}>
                                                                <span className="plan-name">{plan.name}</span>
                                                                <span className="plan-header-circle-graphic">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="84" height="57" viewBox="0 0 84 57" fill="none">
                                                                        <path opacity="0.9" d="M0.749996 90.5C0.749998 40.9325 40.9324 0.750002 90.5 0.749996C140.068 0.749994 180.25 40.9325 180.25 90.5C180.25 140.068 140.068 180.25 90.5 180.25C40.9324 180.25 0.749998 140.068 0.749996 90.5Z" fill="white" fillOpacity="0.5" stroke="white" strokeWidth="1.5" />
                                                                    </svg>
                                                                </span>
                                                                <span className="plan-header-circle-graphic">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="59" height="32" viewBox="0 0 59 32" fill="none">
                                                                        <circle cx="66.0006" cy="66.0014" r="66.0006" transform="rotate(-90 66.0006 66.0014)" fill="white" fillOpacity="0.3" />
                                                                        <circle cx="66.0006" cy="66.0014" r="65.2506" transform="rotate(-90 66.0006 66.0014)" stroke="white" strokeOpacity="0.7" strokeWidth="1.5" />
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                            <div className="plan-card-body">
                                                                {/* <img src="images/undraw/1.svg" width="250" className="mx-auto" /> */}
                                                                <div className="d-flex justify-content-between flex-column align-items-start" style={{ minHeight: '3rem' }}>
                                                                    {
                                                                        plan.frequencies?.[frequency]?.original_amount !== plan.frequencies?.[frequency]?.amount &&
                                                                        <span className="plan-original-amount">₹ {plan['frequencies']?.[frequency]?.['original_amount']}</span>
                                                                    }
                                                                    <div className="d-flex align-items-baseline">
                                                                        <span className="plan-amount">
                                                                            ₹{toValidNumber(plan.frequencies?.[frequency]?.amount)}
                                                                        </span>
                                                                        <span className="plan-frequency-text">/mo</span>
                                                                    </div>
                                                                    {
                                                                        plan.frequencies?.[frequency]?.original_amount !== plan.frequencies?.[frequency]?.amount &&
                                                                        <span className="plan-discount-text">Save {plan['frequencies']?.[frequency]?.['discount']}% • ₹{calculateDiscount((Number(plan['frequencies']?.[frequency]?.['original_amount']) ?? 0), (Number(plan['frequencies']?.[frequency]?.['amount']) ?? 0))} OFF</span>
                                                                    }
                                                                </div>

                                                                <div className={`plan-description ${plan.name.toLowerCase()} mt-075`}>
                                                                    <span className="plan-description-text text-start">
                                                                        {(plan.description || "").split('\\n').map((line, idx) => (
                                                                            <span key={idx}>
                                                                                {line}
                                                                                {idx < (plan.description || "").split('\\n').length - 1 && <br />}
                                                                            </span>
                                                                        ))}

                                                                    </span>
                                                                </div>

                                                                <div className="mt-20 d-flex flex-column gap-075">
                                                                    {
                                                                        pricing.features && pricing.features.filter((sf: SubscriptionFeature) => sf.is_visible).map((sf: SubscriptionFeature, i: number) => {
                                                                            return (
                                                                                <span className="text-start text-dark fs-14 fw-400" key={i}>
                                                                                    {
                                                                                        plan.features?.[sf.id] ? <i className="fa fa-check-circle color-green-dark me-2"></i> : <i className="fa fa-times-circle color-red-dark me-2"></i>
                                                                                    }
                                                                                    <span>{sf.name}</span>
                                                                                    {
                                                                                        sf.type === 'consumable' && <span>- <b className="fw-600">{plan.features?.[sf.id]?.value}</b> per month</span>
                                                                                    }
                                                                                </span>
                                                                            )
                                                                        })
                                                                    }
                                                                </div>
                                                                <div className="d-flex justify-content-center mt-4">
                                                                    {
                                                                        (plan.id === pricing.active_plan?.plan.id && frequency === pricing?.active_plan?.plan_frequency?.frequency_slug) ?
                                                                            <button className="btn btn-primary w-100 text-uppercase" disabled>Current Plan</button> :
                                                                            <button onClickCapture={updateIntendedURL} onClick={() => navigate('/register')} className={`btn w-100 text-uppercase btn-sm ${plan.featured ? 'bg-green-dark rounded-sm' : 'btn-primary'}`}>
                                                                                {
                                                                                    plan.id === pricing.active_plan?.plan.id ? "Upgrade Plan" : <>{'Choose'} {plan.name}</>
                                                                                }
                                                                            </button>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </SwiperSlide>
                                                )
                                            })
                                        }
                                    </Swiper>
                                </div>
                                {
                                    (isIOS()) &&
                                    <>
                                        <div className="divider my-2"></div>
                                        <div className="px-3 my-2">
                                            <p className="text-muted mb-0 font-10">Payment will be charged to your Apple ID account.</p>
                                            <p className="text-muted mb-0 font-10">Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current period.</p>
                                            <p className="text-muted mb-2 font-10">You can manage or cancel your subscription in Account Settings.</p>

                                            <a href="https://influns.com/legal/privacy.html" className="fs-12 text-primary" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                            {" | "}
                                            <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" className="fs-12 text-primary" target="_blank" rel="noopener noreferrer">Terms of Use</a>
                                        </div>
                                    </>
                                }
                            </div>
                        </>
                    )
            }
        </div>
    )
}

export default PricingPublic